import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';

import type {
  SearchResponseFilterOutput,
  SearchResultOrder,
} from '@iconik-io/iconik-api/src/services/search';

import { SEARCH_PAGE_ROUTE } from '../../constants/routes';
import type { AppState } from '../../store/types';
import { selectIsNltfActive } from '../nltf/selectors';
import { getIsNaturalSearchAvailable } from '../settings/select_features';
import { describeNaturalFilterValues, isNaturalWordFilter } from './describe';
import type {
  NaturalSearchInterpretations,
  NaturalSearchSession,
  NaturalSearchState,
  SearchNaturalSearchSegment,
} from './types';

const NO_SEGMENTS: NaturalSearchSession['segmentsById'] = {};
const NO_FILTERS: SearchResponseFilterOutput[] = [];

const selectNaturalSearch = (state: AppState): NaturalSearchState =>
  state.naturalSearch;

export const selectNaturalSearchIsLoading = (state: AppState): boolean =>
  selectNaturalSearch(state).isLoading;

export const selectNaturalSearchSession = (
  state: AppState,
): NaturalSearchSession | null => selectNaturalSearch(state).session;

export const selectNaturalSearchQuery = (state: AppState): string =>
  selectNaturalSearchSession(state)?.query ?? '';

/** Whether a query submitted now would run as one, not whether one has. */
export const selectIsNaturalSearchMode = (state: AppState): boolean =>
  getIsNaturalSearchAvailable(state) && selectIsNltfActive(state);

/**
 * Whether the search results are a natural search's, so they must be read,
 * refreshed and paged as one. True while one is loading or once it has
 * answered.
 *
 * Scoped to the search page: the same grid also renders collections, saved
 * searches and favourites, and those must keep showing their own contents even
 * while a natural-search session is still held in the store. `runNaturalSearch`
 * navigates here first, so a search started elsewhere still lands on screen.
 */
export const selectIsNaturalSearchActive = (state: AppState): boolean => {
  if (!getIsNaturalSearchAvailable(state)) {
    return false;
  }
  if (state.routeParams.route !== SEARCH_PAGE_ROUTE) {
    return false;
  }
  const naturalSearch = selectNaturalSearch(state);
  return naturalSearch.isLoading || naturalSearch.session !== null;
};

/** Whether the natural-search answer on screen has further pages to ask for. */
export const selectCanPageNaturalSearch = (state: AppState): boolean =>
  selectNaturalSearchSession(state)?.replay?.pagination.supported ?? false;

/** The order a natural-search answer is shown in, once there is one to replay. */
export const selectNaturalSearchOrder = (
  state: AppState,
): SearchResultOrder | null =>
  selectNaturalSearchSession(state)?.replay?.filters.order ?? null;

const NO_INTERPRETATIONS: NaturalSearchInterpretations = {
  list: [],
  active: null,
  chosen: false,
};

const selectNaturalSearchInterpretations = (
  state: AppState,
): NaturalSearchInterpretations =>
  selectNaturalSearchSession(state)?.interpretations ?? NO_INTERPRETATIONS;

export type NaturalSearchFilterEntry = {
  entry: SearchResponseFilterOutput;
  isPaused: boolean;
};

const COLLECTION_SCOPE_FIELDS = new Set([
  'ancestor_collections',
  'in_collections',
]);

// Only what narrows the search to collections: one kept out of them reads as
// any other filter.
const isCollectionScope = ({ field, predicate }: SearchResponseFilterOutput) =>
  COLLECTION_SCOPE_FIELDS.has(field) &&
  'op' in predicate &&
  predicate.op !== 'exists' &&
  !predicate.negate;

/**
 * The filters of the answer on screen the panel has no control for: ranking
 * signals, which only ordered the results, and filters that scoped them on a
 * field the panel cannot show (a collection, file names…). Each one lists
 * whether the user paused it.
 */
const selectNaturalSearchFiltersWithoutControl = createSelector(
  selectNaturalSearchSession,
  (session) => {
    const projection = session?.projection;
    if (!projection) {
      return { signals: [], filters: [], collections: [] };
    }
    const projected = Object.values(projection.sourcesByKey).flat();
    const signals: NaturalSearchFilterEntry[] = [];
    const filters: NaturalSearchFilterEntry[] = [];
    const collections: NaturalSearchFilterEntry[] = [];
    for (const entry of projection.applied) {
      // The query's words, and the default lifecycle scope the search always
      // applies, are not filters the user set or would pause.
      if (!('op' in entry.predicate) || entry.field === 'status') {
        continue;
      }
      if (projected.some((source) => isEqual(source, entry))) {
        continue;
      }
      const isPaused = session.adjustments.paused.some((paused) =>
        isEqual(paused, entry),
      );
      if (entry.role === 'scoring') {
        signals.push({ entry, isPaused });
      } else if (isCollectionScope(entry)) {
        collections.push({ entry, isPaused });
      } else {
        filters.push({ entry, isPaused });
      }
    }
    return { signals, filters, collections };
  },
);

/** The ranking signals of the answer on screen: filters that only ordered the results. */
export const selectNaturalSearchSignals = (
  state: AppState,
): NaturalSearchFilterEntry[] =>
  selectNaturalSearchFiltersWithoutControl(state).signals;

/**
 * Filters of the answer on screen that scoped the results on a field the panel
 * cannot show, which the filter chips list beside the panel's. The collections
 * it searched in are not among them: the search bar shows those, as it does
 * for a search made from a collection.
 */
export const selectNaturalSearchHiddenFilters = (
  state: AppState,
): NaturalSearchFilterEntry[] =>
  selectNaturalSearchFiltersWithoutControl(state).filters;

/** The filters the answer understood but gave up on, and the user did not apply anyway. */
export const selectNaturalSearchRelaxedFilters = createSelector(
  (state: AppState) =>
    selectNaturalSearchSession(state)?.replay?.filters.relaxed ?? NO_FILTERS,
  (state: AppState) =>
    selectNaturalSearchSession(state)?.adjustments.added ?? NO_FILTERS,
  (relaxed, added): SearchResponseFilterOutput[] =>
    relaxed.filter(
      (entry) =>
        !added.some(
          (applied) =>
            applied.field === entry.field &&
            isEqual(applied.predicate, entry.predicate),
        ),
    ),
);

export type NaturalSearchReadings = {
  /** What the results on screen answer: the reading, or the query itself. */
  shown: string;
  /** The other readings, offered until the user picks one. */
  alternatives: { index: number; why: string }[];
  /** The words the answer gave up on, to search for the query as typed. */
  typedWords: SearchResponseFilterOutput[];
  /** The other filters it gave up on, which the user can apply anyway. */
  relaxedFilters: SearchResponseFilterOutput[];
  /** Whether the user can keep the reading on screen, which drops the others. */
  canKeep: boolean;
};

/** How the title row describes the answer on screen, and what it offers instead. */
export const selectNaturalSearchReadings = createSelector(
  selectNaturalSearchQuery,
  selectNaturalSearchInterpretations,
  selectNaturalSearchRelaxedFilters,
  (query, { list, active, chosen }, relaxed): NaturalSearchReadings => {
    const alternatives = chosen
      ? []
      : list.flatMap((reading, index) =>
          index !== active && reading.why && reading.filters
            ? [{ index, why: reading.why }]
            : [],
        );
    const typedWords = chosen ? [] : relaxed.filter(isNaturalWordFilter);
    const activeWhy = active === null ? undefined : list[active]?.why;
    return {
      shown: activeWhy || query,
      alternatives,
      typedWords,
      relaxedFilters: relaxed.filter((entry) => !isNaturalWordFilter(entry)),
      canKeep:
        Boolean(activeWhy) &&
        (alternatives.length > 0 || typedWords.length > 0),
    };
  },
);

export const selectNaturalSearchSegmentsById = (
  state: AppState,
): NaturalSearchSession['segmentsById'] =>
  selectNaturalSearchSession(state)?.segmentsById ?? NO_SEGMENTS;

export const selectNaturalSearchSegments = (
  state: AppState,
  itemId: string,
): SearchNaturalSearchSegment[] | undefined =>
  selectNaturalSearchSegmentsById(state)[itemId];

export const selectNaturalSearchProxyUrl = (
  state: AppState,
  assetId: string,
): string | null | undefined =>
  selectNaturalSearchSession(state)?.proxyUrlByAssetId[assetId];

export type NaturalSearchCollectionScope = {
  entries: SearchResponseFilterOutput[];
  /** The collections' names, as the answer gave them, or their ids. */
  titles: string[];
  /** Whether the user widened the answer to all of Iconik. */
  isPaused: boolean;
};

/** The collections the natural-search answer on screen searched in, if any. */
export const selectNaturalSearchCollectionScope = createSelector(
  selectIsNaturalSearchActive,
  (state: AppState) =>
    selectNaturalSearchFiltersWithoutControl(state).collections,
  (isActive, collections): NaturalSearchCollectionScope | null => {
    if (!isActive || !collections.length) {
      return null;
    }
    const titles = collections.flatMap(({ entry }) =>
      describeNaturalFilterValues(entry),
    );
    return {
      entries: collections.map(({ entry }) => entry),
      titles: [...new Set(titles)],
      isPaused: collections.every(({ isPaused }) => isPaused),
    };
  },
);
